import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';

import { environment } from '@environments/environment';
import { ApiResponse } from '@core/types';
import { PaginationConfig, PaginationResult } from './unit.service';
import { LegalComplianceItem, LegalComplianceItemCreate, LegalComplianceItemUpdate } from '@core/types/legal-compliance.type';

@Injectable({ providedIn: 'root' })
export class LegalComplianceService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api}/legal-compliance`;

  grid(config: PaginationConfig): Observable<ApiResponse<PaginationResult<LegalComplianceItem>>> {
    let params = new HttpParams()
      .set('pageNumber', config.pageNumber.toString())
      .set('pageSize', config.pageSize.toString());

    if (config.searchTerm) params = params.set('searchTerm', config.searchTerm);

    if (config.filter) {
      Object.entries(config.filter as Record<string, any>).forEach(([k, v]) => {
        if (v === null || v === undefined || v === '') return;
        params = params.set(`filter.${k}`, String(v));
      });
    }

    return this.http.get<ApiResponse<PaginationResult<LegalComplianceItem>>>(`${this.baseUrl}/grid`, { params }).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error loading legal compliance items')
      )
    );
  }

  get(id: string): Observable<ApiResponse<LegalComplianceItem>> {
    return this.http.get<ApiResponse<LegalComplianceItem>>(`${this.baseUrl}/${id}`).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error loading legal compliance item')
      )
    );
  }

  create(data: LegalComplianceItemCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(this.baseUrl, data).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error creating legal compliance item')
      )
    );
  }

  update(data: LegalComplianceItemUpdate): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(this.baseUrl, data).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error updating legal compliance item')
      )
    );
  }

  delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error deleting legal compliance item')
      )
    );
  }
}

